import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestResultsPanel = ({ testResults, onClearResults }) => {
  const handleExport = () => {
    const exportData = {
      timestamp: new Date()?.toISOString(),
      totalTests: testResults?.length,
      results: testResults
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `violation-test-results-${Date.now()}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const completedTests = testResults?.filter(r => r?.status === 'completed')?.length || 0;
  const failedTests = testResults?.filter(r => r?.status === 'failed')?.length || 0;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="FileText" size={20} />
              Test Results & Analytics
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Comprehensive testing reports and notification audit trails
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleExport}
              disabled={testResults?.length === 0}
            >
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              onClick={onClearResults}
              disabled={testResults?.length === 0}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Total Tests</p>
            <p className="text-2xl font-bold text-foreground">{testResults?.length}</p>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Successful</p>
            <p className="text-2xl font-bold text-success">{completedTests}</p>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Failed</p>
            <p className="text-2xl font-bold text-error">{failedTests}</p>
          </div>
        </div>
      </div>

      {/* Test Results List */}
      <div className="p-4">
        {testResults?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="ClipboardList" size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No test results yet</p>
            <p className="text-xs mt-1">Run violation tests to see results here</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {testResults?.map((result, index) => (
              <div
                key={result?.id || index}
                className="p-4 rounded-lg border border-border bg-background hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        result?.status === 'completed' ? 'bg-green-100 text-green-700' :
                        result?.status === 'failed'? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {result?.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(result?.timestamp)?.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {result?.triggerType?.replace(/_/g, ' ')?.toUpperCase()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Source: {result?.detectionSource}
                    </p>
                  </div>
                  {result?.status === 'completed' && (
                    <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                  )}
                  {result?.status === 'failed' && (
                    <Icon name="XCircle" size={24} color="var(--color-error)" />
                  )}
                </div>

                {/* Notification Status */}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon
                        name={result?.alertSent ? 'CheckCircle2' : 'Circle'}
                        size={14}
                        color={result?.alertSent ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <span className={result?.alertSent ? 'text-success' : 'text-muted-foreground'}>
                        Alert Sent
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon
                        name={result?.adminNotified ? 'CheckCircle2' : 'Circle'}
                        size={14}
                        color={result?.adminNotified ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <span className={result?.adminNotified ? 'text-success' : 'text-muted-foreground'}>
                        Admin Notified
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon
                        name={result?.clientNotified ? 'CheckCircle2' : 'Circle'}
                        size={14}
                        color={result?.clientNotified ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <span className={result?.clientNotified ? 'text-success' : 'text-muted-foreground'}>
                        Client Notified
                      </span>
                    </div>
                  </div>
                </div>

                {/* Affected Employees */}
                {result?.affectedEmployeeIds?.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Affected employees: {result?.affectedEmployeeIds?.length}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResultsPanel;